// biome-ignore-all lint/a11y/noLabelWithoutControl: each <label> captions the custom input directly beside it in the review panel (the codebase's form-file convention).
// =============================================================================
// ByodLookupPanel.jsx — the "BYOD AI" lookup + stored-band panel (BYOD Stage 3 + Part 8/9)
// -----------------------------------------------------------------------------
// Shown on a child whose school was chosen from the list (school_id set). It:
//   • resolves the child's year to the school's ACTIVE covering band (db-198/199)
//     and shows it inline when fresh — NO AI call (the reuse win); a "View" button opens
//     the band laid out in a read-only draggable modal (Part 9);
//   • when a band is STALE (past the policy-year rollover) it does NOT show the old data —
//     it says the current policy is out of date and requires a fresh lookup (Part 9);
//   • a lookup either searches the WHOLE school (`lookup_bands`, fills every year band) or
//     only the CHILD'S YEAR (`lookup`), per the `byod_responder_whole_school` switch
//     (default = child's year only, faster — Part 9). Either way it auto-saves (source
//     "ai") any band not already stored, then opens the result in the modal.
//
// Part 8b — whether the responder may edit is gated by `byod_responder_can_edit`
// (useByodResponderCanEdit): OFF (default) = read-only (admins correct via the Edit-School
// manager); ON = the responder can open the covering band and edit/save it ("responder").
//
// The caller gates whether this renders at all (the visibility switch + audience); this
// component needs a school_id + the child's year level. Saving goes through the
// service-role EF (internal callers only) — see the caller's gate.
// =============================================================================

import React from "react";
import {
  normalizePolicyReview,
  POLICY_REVIEW_KEY,
  phaseScopeForYear,
} from "../../../supabase/functions/_shared/byod/prompt";
import {
  useByodResponderCanEdit,
  useByodResponderWholeSchool,
} from "../../app/UiConfigContext.jsx";
import { getConfigValue } from "../../data/portalConfig";
import { listActiveBandsForSchool } from "../../data/schoolByod";
import { getSchoolById } from "../../data/schools";
import { isBandStale, resolveCoveringBand } from "../../services/byodBands";
import {
  lookupByod,
  lookupByodBands,
  saveByodBand,
  saveByodBands,
} from "../../services/byodLookup";
import { yearLevelToNumber } from "../../utils/formConstants";
import { ModalBackdrop } from "../form/FormUI.jsx";
import {
  ByodBandEditor,
  bandDraftFromBand,
  bandDraftFromSuggestion,
  bandDraftToFields,
  bandLabel,
  TEAL,
  validateBandDraft,
} from "./ByodBandEditor.jsx";

const REVERSE_BTN = {
  background: TEAL,
  color: "#FFFFFF",
  border: "none",
  borderRadius: 7,
  padding: "7px 14px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
};

function triLabel(v) {
  return v === true ? "Yes" : v === false ? "No" : "Unknown";
}

function statusText(status) {
  return status === "byod"
    ? "Student brings own device"
    : status === "school_supplied"
      ? "School supplies the device"
      : "Unknown";
}

// One label/value row in the read-only "View" modal (Part 9).
function BandRow({ label, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "150px 1fr",
        gap: 10,
        padding: "6px 0",
        borderBottom: "1px solid #EEF3F2",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#607D79",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#1A2E2B", wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}

function ByodLookupPanel({ schoolId, yearLevel }) {
  const [loading, setLoading] = React.useState(true);
  const [band, setBand] = React.useState(null);
  const [stale, setStale] = React.useState(false);
  const [looking, setLooking] = React.useState(false);
  const [result, setResult] = React.useState(null); // { suggestion, confidence, sources, parsed }
  const [edited, setEdited] = React.useState(null); // the editable draft in review mode
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [viewOpen, setViewOpen] = React.useState(false); // Part 9: read-only band modal

  const canEdit = useByodResponderCanEdit(); // Part 8b: responders may edit (ON) vs view-only (OFF)
  const wholeSchool = useByodResponderWholeSchool(); // Part 9: whole-school (ON) vs child's year (OFF)
  const yearNum = yearLevelToNumber(yearLevel);

  // Load the stored active bands + resolve the covering one whenever the school /
  // year changes. Fresh band → shown inline, no AI call.
  const loadBands = React.useCallback(
    async function () {
      if (!schoolId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const [bands, reviewVal] = await Promise.all([
        listActiveBandsForSchool(schoolId),
        getConfigValue(POLICY_REVIEW_KEY),
      ]);
      const rev = normalizePolicyReview(reviewVal);
      const covering = resolveCoveringBand(bands, yearNum);
      const isStale = isBandStale(covering, new Date(), rev.month, rev.day);
      setBand(covering);
      setStale(isStale);
      setLoading(false);
      // Return the FRESH covering band (null when none / stale) so a lookup can decide
      // whether to open the read-only view modal (Part 9).
      return isStale ? null : covering;
    },
    [schoolId, yearNum]
  );

  React.useEffect(
    function () {
      loadBands();
    },
    [loadBands]
  );

  // Part 9: a grounded lookup, either WHOLE-SCHOOL (every band Prep–12 in one call) or only
  // the CHILD'S YEAR, per the `byod_responder_whole_school` switch. Either way the result is
  // auto-saved (source "ai"); when a covering band results we open the read-only view modal.
  const noUsableBandMsg = (parsed, finishReason) =>
    parsed
      ? "The lookup didn't find a usable BYOD band. An admin can add one on the school's Edit screen."
      : finishReason === "MAX_TOKENS"
        ? "The AI reply was cut off before it finished — try again, or ask an admin to look this school up by phase."
        : "Couldn't read a clear answer from the lookup — check the sources or ask an admin.";

  async function runLookup() {
    setError("");
    setLooking(true);
    setResult(null);
    const school = await getSchoolById(schoolId);
    const base = {
      schoolName: school?.name || "",
      suburb: school?.suburb || "",
      state: school?.state || "",
    };

    let meta;
    let saveRes;
    if (wholeSchool) {
      // Part 10 (10a): a responder "whole school" search is scoped to the CHILD'S PHASE
      // (Year <=6 -> Prep-6, >=7 -> 7-12); no year -> fall back to the full 0-12 search.
      const scope = phaseScopeForYear(yearNum);
      const r = await lookupByodBands(scope ? { ...base, ...scope } : base);
      if (!r.ok) {
        setLooking(false);
        setError(r.error || "The lookup didn't return a result. Try again.");
        return;
      }
      meta = {
        confidence: r.confidence,
        sources: r.sources || [],
        parsed: r.parsed,
        finishReason: r.finishReason,
      };
      const bands = (r.suggestions || [])
        .map((s) => bandDraftFromSuggestion(s, null))
        .map((d) => {
          const v = validateBandDraft(d);
          return v.ok ? { fields: bandDraftToFields(d), yearFrom: v.from, yearTo: v.to } : null;
        })
        .filter(Boolean);
      if (bands.length === 0) {
        setLooking(false);
        setResult(meta);
        setError(noUsableBandMsg(r.parsed, r.finishReason));
        await loadBands();
        return;
      }
      saveRes = await saveByodBands({ schoolId, bands, source: "ai" });
    } else {
      // Child's-year-only lookup — the single-band EF path (faster). Default (Part 9).
      const r = await lookupByod({ ...base, yearLevel: yearLevel || "" });
      if (!r.ok || !r.suggestion) {
        setLooking(false);
        setError(r.error || "The lookup didn't return a result. Try again.");
        return;
      }
      meta = {
        confidence: r.confidence,
        sources: r.sources || [],
        parsed: r.parsed,
        finishReason: r.finishReason,
      };
      const draft = bandDraftFromSuggestion(r.suggestion, yearNum);
      const v = validateBandDraft(draft);
      if (!v.ok) {
        setLooking(false);
        setResult(meta);
        setError(noUsableBandMsg(r.parsed, r.finishReason));
        await loadBands();
        return;
      }
      saveRes = await saveByodBand({
        schoolId,
        fields: bandDraftToFields(draft),
        yearFrom: v.from,
        yearTo: v.to,
        source: "ai",
      });
    }

    setLooking(false);
    if (!saveRes.ok) {
      setError(saveRes.error || "Save failed.");
      return;
    }
    setResult(meta); // keep the AI confidence + sources for the view modal / inline line
    const covering = await loadBands(); // re-resolve the covering band from the now-stored set
    if (covering) setViewOpen(true); // Part 9: show the fresh result in the modal first
  }

  // Reopen the full editable form for the CURRENTLY STORED band (not a fresh AI lookup) —
  // so a responder can review it laid out and tweak a field. Saving re-writes the band
  // (deactivate-and-insert), same as an AI result.
  function openEditFromBand() {
    if (!band) return;
    setError("");
    setResult(null);
    setEdited(bandDraftFromBand(band));
  }

  function setField(key, value) {
    setEdited(function (prev) {
      return Object.assign({}, prev, { [key]: value });
    });
  }

  async function saveBand() {
    if (!edited) return;
    const v = validateBandDraft(edited);
    if (!v.ok) {
      setError(v.error);
      return;
    }
    setError("");
    setSaving(true);
    const res = await saveByodBand({
      schoolId,
      fields: bandDraftToFields(edited),
      yearFrom: v.from,
      yearTo: v.to,
      source: "responder",
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setResult(null);
    setEdited(null);
    await loadBands(); // re-show the now-stored fresh band
  }

  if (!schoolId) return null;

  const wrap = {
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 8,
    background: "#F2FAF9",
    border: "1px solid #B2DFDB",
  };

  if (loading) {
    return (
      <div style={wrap}>
        <span style={{ fontSize: 12, color: "#607D79" }}>Checking BYOD…</span>
      </div>
    );
  }

  // ── REVIEW / EDIT MODE — editable band (from an AI result OR the stored band) ─
  if (edited) {
    return (
      <div style={wrap}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          {result ? (
            <>
              <span
                style={{
                  background: "#FFF3E0",
                  border: "1px solid #FFB300",
                  borderRadius: 5,
                  padding: "2px 8px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#7c4a00",
                }}
              >
                AI — verify
              </span>
              <span style={{ fontSize: 12, color: "#607D79" }}>
                Confidence: {result.confidence || "low"}
              </span>
              {!result.parsed && (
                <span style={{ fontSize: 12, color: "#C62828" }}>
                  (
                  {result.finishReason === "MAX_TOKENS"
                    ? "reply was cut off — check the sources"
                    : "couldn't read a clear answer — check the sources"}
                  )
                </span>
              )}
            </>
          ) : (
            <span style={{ fontSize: 12, fontWeight: 700, color: "#00695C" }}>Edit BYOD band</span>
          )}
        </div>

        <ByodBandEditor edited={edited} setField={setField} />

        {result?.sources && result.sources.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#607D79" }}>
            Sources:{" "}
            {result.sources.map((u, i) => (
              <a
                key={u}
                href={u}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: TEAL, marginRight: 8 }}
              >
                [{i + 1}]
              </a>
            ))}
          </div>
        )}

        {error && <div style={{ marginTop: 8, fontSize: 12, color: "#C62828" }}>{error}</div>}

        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button type="button" style={REVERSE_BTN} disabled={saving} onClick={saveBand}>
            {saving ? "Saving…" : "Save to school record"}
          </button>
          <button
            type="button"
            style={{
              background: "#fff",
              color: "#607D79",
              border: "1px solid #CFE0DE",
              borderRadius: 7,
              padding: "7px 14px",
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
            disabled={saving}
            onClick={() => {
              setResult(null);
              setEdited(null);
              setError("");
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── FRESH BAND / STALE / NO BAND ────────────────────────────────────────────
  const linkStyle = { fontSize: 13, color: TEAL, fontWeight: 600 };
  const viewBtn = {
    background: "#fff",
    color: "#00695C",
    border: "1px solid #4DB6AC",
    borderRadius: 7,
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <div style={wrap}>
      {band && !stale ? (
        // A FRESH covering band — shown inline (unchanged) + a View button (Part 9).
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#00695C" }}>
              BYOD — {bandLabel(band.year_from, band.year_to)}
            </span>
            <span style={{ fontSize: 11, color: "#90A4A0" }}>
              checked {band.byod_checked_at ? String(band.byod_checked_at).slice(0, 10) : "—"}
            </span>
          </div>
          <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5 }}>
            <strong>{statusText(band.byod_status)}</strong>
            {band.byod_device ? ` · ${band.byod_device}` : ""}
            {` · Loan: ${triLabel(band.byod_loan)} · Hardship: ${triLabel(band.byod_hardship)}`}
            {band.byod_loan_note ? ` · ${band.byod_loan_note}` : ""}
          </div>
          {band.byod_notes && (
            <div style={{ fontSize: 12, color: "#607D79", marginTop: 4 }}>{band.byod_notes}</div>
          )}
          {result && (
            <div style={{ fontSize: 11, color: "#607D79", marginTop: 4 }}>
              AI · confidence {result.confidence || "low"}
              {!result.parsed &&
                (result.finishReason === "MAX_TOKENS"
                  ? " · reply was cut off"
                  : " · couldn't read a clear answer")}
            </div>
          )}
          <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center" }}>
            {band.byod_policy_url && (
              <a
                href={band.byod_policy_url}
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
              >
                Policy page ↗
              </a>
            )}
            <button type="button" style={viewBtn} onClick={() => setViewOpen(true)}>
              View
            </button>
            {canEdit && (
              <button type="button" style={viewBtn} onClick={openEditFromBand}>
                Edit
              </button>
            )}
          </div>
        </div>
      ) : band && stale ? (
        // STALE — do NOT show the old policy; require a fresh lookup (Part 9).
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#7c4a00" }}>
            Our current BYOD policy is out of date
          </span>
          <button type="button" style={REVERSE_BTN} disabled={looking} onClick={runLookup}>
            {looking ? "Looking up…" : "Re-run BYOD AI"}
          </button>
        </div>
      ) : (
        // NO band on file.
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#00695C" }}>
            Lookup Result – NO BYOD Policy found
          </span>
          <button type="button" style={REVERSE_BTN} disabled={looking} onClick={runLookup}>
            {looking ? "Looking up…" : "BYOD AI"}
          </button>
        </div>
      )}
      {error && <div style={{ marginTop: 8, fontSize: 12, color: "#C62828" }}>{error}</div>}

      {/* Part 9 — read-only, draggable "View" modal (no edit, no refresh) */}
      {viewOpen && band && (
        <ModalBackdrop
          onClose={() => setViewOpen(false)}
          portal
          draggable
          dragTitle={`BYOD Policy — ${bandLabel(band.year_from, band.year_to)}`}
          contentStyle={{ maxWidth: 560 }}
        >
          <div style={{ background: "#fff", borderRadius: 10, padding: "14px 18px" }}>
            <BandRow label="Year band">{bandLabel(band.year_from, band.year_to)}</BandRow>
            <BandRow label="Policy">{statusText(band.byod_status)}</BandRow>
            <BandRow label="Device">{band.byod_device || "—"}</BandRow>
            <BandRow label="Loan / hire">
              {triLabel(band.byod_loan)}
              {band.byod_loan_note ? ` — ${band.byod_loan_note}` : ""}
            </BandRow>
            <BandRow label="Hardship provision">{triLabel(band.byod_hardship)}</BandRow>
            <BandRow label="Policy page">
              {band.byod_policy_url ? (
                <a
                  href={band.byod_policy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  Open policy page ↗
                </a>
              ) : (
                "—"
              )}
            </BandRow>
            <BandRow label="Notes">{band.byod_notes || "—"}</BandRow>
            <BandRow label="Checked">
              {band.byod_checked_at ? String(band.byod_checked_at).slice(0, 10) : "—"}
              {band.byod_policy_year ? ` · policy year ${band.byod_policy_year}` : ""}
            </BandRow>
            {result && (
              <BandRow label="AI lookup">
                confidence {result.confidence || "low"}
                {!result.parsed &&
                  (result.finishReason === "MAX_TOKENS"
                    ? " · reply was cut off"
                    : " · couldn't read a clear answer")}
                {result.sources && result.sources.length > 0 && (
                  <>
                    {" · sources: "}
                    {result.sources.map((u, i) => (
                      <a
                        key={u}
                        href={u}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: TEAL, marginRight: 6 }}
                      >
                        [{i + 1}]
                      </a>
                    ))}
                  </>
                )}
              </BandRow>
            )}
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                style={{
                  background: TEAL,
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  padding: "8px 18px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
                onClick={() => setViewOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}

export { ByodLookupPanel };
